// src/constants/severity.js

import { isRevenueCriticalCategory } from "./lanes";

/**
 * Incident severity.
 *
 * Severity describes how much damage the incident is doing right now. It drives
 * queue ordering, visual prominence, and which incidents get escalated first.
 *
 * It deliberately carries no response-time target: we do not staff to a
 * published SLA yet, and showing a countdown we cannot honour is worse than
 * showing none. When we do commit to targets, add them to the level definitions
 * below — every surface reads severity through this module, so the timers appear
 * everywhere at once.
 */

export const SEVERITY_IDS = {
  P1: "P1",
  P2: "P2",
  P3: "P3",
  P4: "P4",
};

/**
 * `rank` is the sort key: 1 is the most severe, so ascending rank puts the
 * worst incidents at the top of a queue.
 */
export const SEVERITY_LEVELS = {
  [SEVERITY_IDS.P1]: {
    id: SEVERITY_IDS.P1,
    rank: 1,
    shortLabel: "P1",
    name: "Critical",
    label: "P1 · Critical",
    description:
      "Revenue has stopped or customers cannot complete payment. Needs someone on it now.",
    chipClass: "bg-rose-50 text-rose-700 border-rose-200",
    dotClass: "bg-rose-500",
    accentClass: "border-l-rose-500",
  },
  [SEVERITY_IDS.P2]: {
    id: SEVERITY_IDS.P2,
    rank: 2,
    shortLabel: "P2",
    name: "High",
    label: "P2 · High",
    description:
      "A revenue-critical path is degraded or failing for a subset of customers.",
    chipClass: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
    accentClass: "border-l-amber-500",
  },
  [SEVERITY_IDS.P3]: {
    id: SEVERITY_IDS.P3,
    rank: 3,
    shortLabel: "P3",
    name: "Moderate",
    label: "P3 · Moderate",
    description:
      "Something is broken or misbehaving, but money is still moving correctly.",
    chipClass: "bg-sky-50 text-sky-700 border-sky-200",
    dotClass: "bg-sky-500",
    accentClass: "border-l-sky-500",
  },
  [SEVERITY_IDS.P4]: {
    id: SEVERITY_IDS.P4,
    rank: 4,
    shortLabel: "P4",
    name: "Low",
    label: "P4 · Low",
    description:
      "Question, cleanup, or hardening work with no active customer impact.",
    chipClass: "bg-slate-50 text-slate-700 border-slate-200",
    dotClass: "bg-slate-400",
    accentClass: "border-l-slate-400",
  },
};

/** Most severe first. */
export const SEVERITY_ORDER = [
  SEVERITY_IDS.P1,
  SEVERITY_IDS.P2,
  SEVERITY_IDS.P3,
  SEVERITY_IDS.P4,
];

const SEVERITY_BY_URGENCY = {
  emergency: SEVERITY_IDS.P1,
  critical: SEVERITY_IDS.P1,
  high: SEVERITY_IDS.P2,
  medium: SEVERITY_IDS.P3,
  moderate: SEVERITY_IDS.P3,
  low: SEVERITY_IDS.P4,
};

const DEFAULT_SEVERITY = SEVERITY_IDS.P3;

export function isValidSeverity(severityId) {
  return Boolean(severityId && SEVERITY_LEVELS[severityId]);
}

/** Raises severity by one level; P1 is already the ceiling. */
function escalate(severityId) {
  const index = SEVERITY_ORDER.indexOf(severityId);
  if (index <= 0) return SEVERITY_IDS.P1;
  return SEVERITY_ORDER[index - 1];
}

/**
 * Maps reported urgency plus incident category onto a severity level.
 *
 * Urgency is what the customer told us. The category bump is what we know: a
 * failure on the revenue path is one level worse than the customer's own
 * assessment, because customers routinely under-report checkout and payout
 * breakage as "medium" when it is actively costing them money.
 */
export function deriveSeverity({ urgency, categoryId } = {}) {
  const normalized = String(urgency || "").toLowerCase().trim();
  const base = SEVERITY_BY_URGENCY[normalized] || DEFAULT_SEVERITY;

  if (base !== SEVERITY_IDS.P1 && isRevenueCriticalCategory(categoryId)) {
    return escalate(base);
  }
  return base;
}

/**
 * Resolves the severity level object for a problem document.
 *
 * Prefers the stored `severity` field, and falls back to deriving it from
 * `urgency` and `categoryId` so incidents created before severity existed still
 * render correctly without a backfill.
 */
export function getIncidentSeverity(problem) {
  const stored = problem?.severity;
  if (isValidSeverity(stored)) {
    return SEVERITY_LEVELS[stored];
  }
  return SEVERITY_LEVELS[deriveSeverity(problem || {})];
}

/** Comparator for queues: most severe first, then oldest first. */
export function compareBySeverity(a, b) {
  const rankDelta = getIncidentSeverity(a).rank - getIncidentSeverity(b).rank;
  if (rankDelta !== 0) return rankDelta;
  return (a?.createdAt?.seconds || 0) - (b?.createdAt?.seconds || 0);
}
